import React, { createContext, useContext, useEffect, useState } from 'react';
import storage, { STORAGE_KEYS } from '../utils/storage';

type LayoutMode = 'list' | 'grid' | 'classic';

interface LayoutContextType {
  layout: LayoutMode;
  setLayout: (l: LayoutMode) => Promise<void>;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

function applyLayoutToRoot(l: LayoutMode) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-layout', l);
}

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [layout, setLayoutState] = useState<LayoutMode>('list');

  useEffect(() => {
    let mounted = true;
    (async () => {
      const saved = await storage.getItem(STORAGE_KEYS.LAYOUT);
      if (!mounted) return;
      if (saved === 'list' || saved === 'grid' || saved === 'classic') {
        setLayoutState(saved as LayoutMode);
        applyLayoutToRoot(saved as LayoutMode);
      } else {
        applyLayoutToRoot('list');
      }
    })();
    return () => { mounted = false; };
  }, []);

  const setLayout = async (l: LayoutMode) => {
    setLayoutState(l);
    applyLayoutToRoot(l);
    try { await storage.setItem(STORAGE_KEYS.LAYOUT, l); } catch (e) {}
  };

  return <LayoutContext.Provider value={{ layout, setLayout }}>{children}</LayoutContext.Provider>;
};

export const useLayout = () => {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error('useLayout must be used within LayoutProvider');
  return ctx;
};

export default LayoutProvider;
